import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import useSound from 'use-sound';

// Import sounds
import workStartSound from '../assets/sounds/work-start.mp3';
import breakStartSound from '../assets/sounds/break-start.mp3';
import timerCompleteSound from '../assets/sounds/timer-complete.mp3';

const TimerContext = createContext();

export const TIMER_TYPES = {
  WORK: 'work',
  SHORT_BREAK: 'short_break',
  LONG_BREAK: 'long_break',
};

export const SOUND_THEMES = {
  GARDEN: 'garden',
  KITCHEN: 'kitchen',
  CLASSIC: 'classic',
};

export function TimerProvider({ children }) {
  const [isRunning, setIsRunning] = useState(false);
  const [timerType, setTimerType] = useState(TIMER_TYPES.WORK);
  const [timeLeft, setTimeLeft] = useState(25 * 60); // Initial time in seconds (25 minutes)
  const [settings, setSettings] = useState(() => {
    // Try to get settings from localStorage
    const savedSettings = localStorage.getItem('pomodoro_settings');
    return savedSettings ? JSON.parse(savedSettings) : {
      workDuration: 25,
      shortBreakDuration: 5,
      longBreakDuration: 30,
      pomodorosUntilLongBreak: 4,
      notificationEnabled: true,
      soundEnabled: true,
      soundTheme: SOUND_THEMES.GARDEN,
      soundVolume: 80,
      autoStartBreaks: true,
      autoStartPomodoros: false,
    };
  });
  const [completedPomodoros, setCompletedPomodoros] = useState(0);
  const [currentTask, setCurrentTask] = useState(null);
  
  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('pomodoro_settings', JSON.stringify(settings));
  }, [settings]);
  
  // Sound hooks with proper dependencies
  const [playWorkStart] = useSound(workStartSound, { 
    volume: settings.soundEnabled ? settings.soundVolume / 100 : 0,
    interrupt: true // Allow interrupting previous sounds
  });
  const [playBreakStart] = useSound(breakStartSound, { 
    volume: settings.soundEnabled ? settings.soundVolume / 100 : 0,
    interrupt: true 
  });
  const [playTimerComplete] = useSound(timerCompleteSound, { 
    volume: settings.soundEnabled ? settings.soundVolume / 100 : 0,
    interrupt: true 
  });

  // Save settings to localStorage
  const saveSettings = async (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('pomodoro_settings', JSON.stringify(newSettings));
  };

  // Reset timer based on current timer type
  const resetTimer = useCallback(() => {
    switch (timerType) {
      case TIMER_TYPES.WORK:
        setTimeLeft(settings.workDuration * 60);
        break;
      case TIMER_TYPES.SHORT_BREAK:
        setTimeLeft(settings.shortBreakDuration * 60);
        break;
      case TIMER_TYPES.LONG_BREAK:
        setTimeLeft(settings.longBreakDuration * 60);
        break;
      default:
        setTimeLeft(settings.workDuration * 60);
    }
  }, [timerType, settings.workDuration, settings.shortBreakDuration, settings.longBreakDuration]);

  // Update timer type and reset timer
  const setTimerTypeAndReset = (type) => {
    setTimerType(type);
    setIsRunning(false);
    
    // Force an immediate timer reset based on the timer type
    switch (type) {
      case TIMER_TYPES.WORK:
        setTimeLeft(settings.workDuration * 60);
        break;
      case TIMER_TYPES.SHORT_BREAK:
        setTimeLeft(settings.shortBreakDuration * 60);
        break;
      case TIMER_TYPES.LONG_BREAK:
        setTimeLeft(settings.longBreakDuration * 60);
        break;
      default:
        setTimeLeft(settings.workDuration * 60);
    }
  };

  // Handle timer completion
  const handleTimerComplete = useCallback(() => {
    console.log("Timer completed!", { timerType, completedPomodoros });
    
    // Play timer complete sound regardless of settings to ensure it works
    // The volume will be 0 if sound is disabled
    playTimerComplete();
    
    // Add a backup method using the Audio API
    if (settings.soundEnabled) {
      try {
        const audio = new Audio(timerCompleteSound);
        audio.volume = settings.soundVolume / 100;
        audio.play().catch(e => console.log("Audio play error:", e));
      } catch (error) {
        console.log("Error playing completion sound:", error);
      }
    }

    // If notification is enabled, show notification
    if (settings.notificationEnabled) {
      if ('Notification' in window && Notification.permission === 'granted') {
        let title, body;

        if (timerType === TIMER_TYPES.WORK) {
          title = 'Work session complete!';
          body = 'Time for a break.';
        } else {
          title = 'Break complete!';
          body = 'Ready to work again?';
        }

        new Notification(title, { body });
      }
    }

    if (timerType === TIMER_TYPES.WORK) {
      // Increment completed pomodoros
      const newCompletedPomodoros = completedPomodoros + 1;
      setCompletedPomodoros(newCompletedPomodoros);

      // Update task if there is one
      if (currentTask) {
        updateTaskProgress(currentTask.id);
      }
      
      // Determine next timer type (short break or long break)
      // We want long break after the 4th pomodoro (when completedPomodoros becomes 4)
      const nextTimerType = newCompletedPomodoros % settings.pomodorosUntilLongBreak === 0 
        ? TIMER_TYPES.LONG_BREAK 
        : TIMER_TYPES.SHORT_BREAK;
      
      setTimerType(nextTimerType);
      
      // Auto-start break if enabled
      if (settings.autoStartBreaks) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    } else if (timerType === TIMER_TYPES.LONG_BREAK) {
      // After long break, reset pomodoro count and switch back to work mode
      setCompletedPomodoros(0);
      setTimerType(TIMER_TYPES.WORK);
      
      // Auto-start work if enabled
      if (settings.autoStartPomodoros) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    } else {
      // After short break, just switch back to work mode
      setTimerType(TIMER_TYPES.WORK);
      
      // Auto-start work if enabled
      if (settings.autoStartPomodoros) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    }
  }, [timerType, completedPomodoros, settings, currentTask, playTimerComplete, timerCompleteSound]);

  // We no longer play sounds on timer start
  // Sounds will be played in the TomatoTimer component when countdown starts

  // Timer effect
  useEffect(() => {
    let interval = null;
    
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTimeLeft) => {
          // Avoid negative timeLeft
          return Math.max(0, prevTimeLeft - 1);
        });
      }, 1000);
    } else if (isRunning && timeLeft === 0) {
      // Handle timer completion when timeLeft reaches zero
      console.log("Timer reached zero - completing");
      handleTimerComplete();
      // Ensure timer doesn't continuously try to complete
      setIsRunning(false);
    } else {
      clearInterval(interval);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, timeLeft, handleTimerComplete]);

  // Reset timer when timer type changes
  useEffect(() => {
    resetTimer();
    // No longer playing sound when timer type changes
  }, [timerType, resetTimer]);

  // Update task progress
  const updateTaskProgress = (taskId) => {
    // Find the task in localStorage and update its completed pomodoros
    const savedTasks = JSON.parse(localStorage.getItem('pomodoro_tasks') || '[]');
    const updatedTasks = savedTasks.map(task => {
      if (task.id === taskId) {
        const newCompletedPomodoros = task.completed_pomodoros + 1;
        const newStatus = newCompletedPomodoros >= task.estimated_pomodoros ? 'completed' : 'in_progress';
        
        return {
          ...task,
          completed_pomodoros: newCompletedPomodoros,
          status: newStatus,
          updated_at: new Date().toISOString()
        };
      }
      return task;
    });
    
    // Save the updated tasks back to localStorage
    localStorage.setItem('pomodoro_tasks', JSON.stringify(updatedTasks));
  };

  // Set the current task
  const startTaskPomodoro = (task) => {
    setCurrentTask(task);
    setTimerType(TIMER_TYPES.WORK);
    resetTimer();
    setIsRunning(false); // Don't auto-start; the countdown component will handle this
  };

  // Force a specific long break duration
  const forceLongBreakDuration = (duration) => {
    console.log(`Setting long break duration to ${duration} minutes`);
    
    // Update settings with the new duration
    const newSettings = { 
      ...settings, 
      longBreakDuration: duration 
    };
    setSettings(newSettings);
    
    // Save to localStorage
    localStorage.setItem('pomodoro_settings', JSON.stringify(newSettings));
    
    // Set timer type to long break
    setTimerType(TIMER_TYPES.LONG_BREAK);
    
    // Directly set the time left to the new duration in seconds
    setTimeLeft(duration * 60);
  };

  const value = {
    isRunning,
    setIsRunning,
    timerType,
    setTimerType: setTimerTypeAndReset,
    timeLeft,
    setTimeLeft,
    completedPomodoros,
    setCompletedPomodoros,
    settings,
    saveSettings,
    resetTimer,
    startTaskPomodoro,
    currentTask,
    forceLongBreakDuration,
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};
